import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'

import { CashFlowProjectionClass } from '@/entities/cashflow-projections'
import { CashFlowProjection } from '@/entities/cashflow-projections/utils/types'
import { EntityState } from '@/entities/utils/enums'

import {
  createBunchElement,
  unshiftBunchElement,
  duplicateBunchElement,
  isDirtyList,
} from '../utils/helpers'
import { useCashFlowProjectionsStore } from '.'

export const useCashFlowProjectionsBunchStore = defineStore(
  'cashflow-projections-bunch',
  () => {
    // INIT

    const cashFlowProjectionsStore = useCashFlowProjectionsStore()

    const cashFlowProjectionsList = computed(() => cashFlowProjectionsStore.getList)

    // STORE

    const initFlag = ref(false)
    const list = ref<Map<string, CashFlowProjectionClass>>(new Map())

    // ACTIONS

    const createElement = (defaults: Partial<CashFlowProjection> = {}) =>
      createBunchElement(list, CashFlowProjectionClass, defaults)

    const unshiftElement = (element: CashFlowProjectionClass) =>
      unshiftBunchElement(element, list)

    const duplicateElement = (id: string) =>
      duplicateBunchElement(id, list, CashFlowProjectionClass)

    // GETTERS

    const getList = computed(() => (list.value.size ? list.value : new Map()))

    const getElementById = (id: string) =>
      list.value.get(id) as CashFlowProjectionClass

    const isDirty = computed(() => isDirtyList(list.value))

    // FILL STORE

    watch(cashFlowProjectionsList, value => {
      const isFirstLoading = list.value.size === 0
      if (!isFirstLoading) {
        // skip new and deleted cash flow projections
        list.value = new Map(Array.from(getList.value.entries()).filter(([,item]) => !item.isNew && (item.state !== EntityState.DELETED)))
      }
      value.forEach(item => {
        const cashFlowProjection = list.value.get(item.id)
        if (!cashFlowProjection) {
          const instance = new CashFlowProjectionClass(item)
          if (isFirstLoading) {
            list.value.set(item.id, instance)
          } else {
            unshiftElement(instance)
          }
          instance.validateData()
        } else {
          cashFlowProjection.forceUpdate(item)
        }
      })
      initFlag.value = true
    })

    // VALIDATION

    const validateAll = () => {
      list.value.forEach(item => {
        item.validateData()
      })
    }

    // CLEAR STORE

    const clear = () => {
      initFlag.value = false
      list.value = new Map()
    }

    return {
      initFlag,

      createElement,
      unshiftElement,
      duplicateElement,

      getList,
      getElementById,
      isDirty,

      validateAll,

      clear,
    }
  },
)
